import React from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { CircularProgress } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { useContextMenu } from 'react-contexify';
import type { Folder, Project } from '../services/folder.service';
import { ITestCase } from '../types/interfaces/testCase.interface';
import { buildNodeKey, parseNodeKey } from '../utils/nodeId';
import {
  ROOT_FOLDER_MENU_ID,
  REGULAR_FOLDER_MENU_ID,
  PROJECT_MENU_ID,
  TESTCASE_MENU_ID,
} from './FolderContextMenu';
import { FolderTreeItem } from './FolderTreeItem';
import { ProjectTreeItem } from './ProjectTreeItem';
import TreeItemLabel from './TreeItemLabel';

interface TestCaseLoadingState {
  loading: boolean;
  data: ITestCase[] | null;
  error?: string | null;
}

interface FolderTreeProps {
  folders: Folder[];
  loading: boolean;
  selectedNodeIds: string[];
  onSelectedItemsChange: (event: React.SyntheticEvent | null, itemIds: string[] | string) => void;
  renamingNodeId: string;
  renameValue: string;
  onRenameChange: (value: string) => void;
  onRenameCancel: () => void;
  onRenameSubmit: () => void;
  cutProjectIds: number[];
  loadedTestCases: Record<number, TestCaseLoadingState>;
  loadProjectTestCases: (projectId: number) => Promise<void>;
  expandedNodeIds: string[];
  onExpandedItemsChange: (event: React.SyntheticEvent, itemIds: string[]) => void;
  unsortedProjects: Project[];
  unsortedProjectsLoading: boolean;
  filterText: string;
  showModifiedDates: boolean;
}

const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  loading,
  selectedNodeIds,
  onSelectedItemsChange,
  renamingNodeId,
  renameValue,
  onRenameChange,
  onRenameCancel,
  onRenameSubmit,
  cutProjectIds,
  loadedTestCases,
  loadProjectTestCases,
  expandedNodeIds,
  onExpandedItemsChange,
  unsortedProjects,
  unsortedProjectsLoading,
  filterText,
  showModifiedDates,
}) => {
  const { show: showRootMenu } = useContextMenu({ id: ROOT_FOLDER_MENU_ID });
  const { show: showRegularMenu } = useContextMenu({ id: REGULAR_FOLDER_MENU_ID });
  const { show: showProjectMenu } = useContextMenu({ id: PROJECT_MENU_ID });
  const { show: showTestCaseMenu } = useContextMenu({ id: TESTCASE_MENU_ID });

  const searchTerm = filterText.trim().toLowerCase();

  const filterFolders = React.useCallback(
    (items: Folder[], ancestorMatched = false): Folder[] => {
      if (!searchTerm || ancestorMatched) return items;
      return items
        .map((f) => {
          const match = f.name.toLowerCase().includes(searchTerm);
          const sub = filterFolders(f.folders || [], ancestorMatched || match);
          const projs = (ancestorMatched || match)
            ? f.projects
            : f.projects.filter((p) => {
                const nameMatch = p.name.toLowerCase().includes(searchTerm) ||
                  String(p.testProjectID).includes(searchTerm);
                const testCaseMatch = loadedTestCases[p.testProjectID]?.data?.some(
                  (tc) =>
                    tc.name?.toLowerCase().includes(searchTerm) ||
                    String(tc.testCaseId).includes(searchTerm)
                );
                return nameMatch || testCaseMatch;
              });
          if (match || sub.length > 0 || projs.length > 0) {
            return { ...f, folders: sub, projects: projs };
          }
          return null;
        })
        .filter(Boolean) as Folder[];
    },
    [searchTerm, loadedTestCases]
  );

  const filterUnsortedProjects = React.useCallback(
    (projects: Project[], ancestorMatched = false): Project[] => {
      if (!searchTerm || ancestorMatched) return projects;
      return projects.filter((p) => {
        const nameMatch = p.name.toLowerCase().includes(searchTerm) ||
          String(p.testProjectID).includes(searchTerm);
        const tcMatch = loadedTestCases[p.testProjectID]?.data?.some(
          (tc) =>
            tc.name?.toLowerCase().includes(searchTerm) ||
            String(tc.testCaseId).includes(searchTerm)
        );
        return nameMatch || tcMatch;
      });
    },
    [searchTerm, loadedTestCases]
  );

  const filteredFolders = React.useMemo(() => filterFolders(folders), [folders, filterFolders]);
  const filteredUnsortedProjects = React.useMemo(
    () => filterUnsortedProjects(unsortedProjects),
    [unsortedProjects, filterUnsortedProjects]
  );

  // Calculate unsorted projects statistics
  const unsortedProjectsStats = React.useMemo(() => {
    if (!showModifiedDates) return null;

    let testCaseCount = 0;
    filteredUnsortedProjects.forEach(project => {
      const testCases = loadedTestCases[project.testProjectID]?.data;
      if (testCases) {
        testCaseCount += testCases.length;
      }
    });

    return {
      projectCount: filteredUnsortedProjects.length,
      testCaseCount
    };
  }, [filteredUnsortedProjects, loadedTestCases, showModifiedDates]);

  const handleContextMenu = (
    event: React.MouseEvent,
    nodeId: string,
    nodeType: 'folder' | 'project' | 'testcase',
    parentId?: number
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (nodeType === 'project') {
      const selectedProjectNodeIds = selectedNodeIds.filter((n) => {
        try { return parseNodeKey(n).type === 'project'; } catch { return false; }
      });
      const projectNodeIdsToUse = selectedProjectNodeIds.includes(nodeId)
        ? selectedProjectNodeIds
        : [nodeId];
      const projectIds = projectNodeIdsToUse.map((nid) => parseNodeKey(nid).id);
      const srcFolderId = parentId !== undefined ? parentId : 0;
      showProjectMenu({ event, props: { projectIds, parentId: srcFolderId } });
    } else if (nodeType === 'testcase') {
      try {
        const { id: testCaseId } = parseNodeKey(nodeId);
        if (parentId !== undefined) {
          showTestCaseMenu({ event, props: { projectId: parentId, testCaseId } });
        }
      } catch (e) {
        console.error("Error parsing test case node ID for context menu", e);
      }
    } else {
      const propsBase = { nodeId, type: 'folder', parentId };
      if (nodeId === 'folder-root') {
        showRootMenu({ event, props: propsBase });
      } else {
        showRegularMenu({ event, props: propsBase });
      }
    }
  };

  const searchExpandedIds = React.useMemo(() => {
    if (!searchTerm) return expandedNodeIds;
    const ids = new Set<string>([...expandedNodeIds, 'folder-root']);

    const projectHasMatch = (project: Project, ancestorMatched: boolean): boolean => {
      const projectMatch = project.name.toLowerCase().includes(searchTerm) ||
        String(project.testProjectID).includes(searchTerm);
      const tcMatch = loadedTestCases[project.testProjectID]?.data?.some(
        (tc) =>
          tc.name?.toLowerCase().includes(searchTerm) ||
          String(tc.testCaseId).includes(searchTerm)
      ) || false;
      if (!projectMatch && tcMatch && !ancestorMatched) {
        ids.add(buildNodeKey({ type: 'project', id: project.testProjectID }));
      }
      return projectMatch || tcMatch;
    };

    const folderHasMatch = (folder: Folder, ancestorMatched: boolean): boolean => {
      const folderMatch = folder.name.toLowerCase().includes(searchTerm);
      const nextAncestor = ancestorMatched || folderMatch;
      let childMatch = false;
      folder.folders?.forEach((sub) => {
        if (folderHasMatch(sub, nextAncestor)) childMatch = true;
      });
      folder.projects.forEach((p) => {
        if (projectHasMatch(p, nextAncestor)) childMatch = true;
      });
      if (childMatch && !folderMatch && !ancestorMatched) {
        ids.add(buildNodeKey({ type: 'folder', id: folder.folderId }));
      }
      return folderMatch || childMatch;
    };

    let unsortedMatch = false;
    unsortedProjects.forEach((p) => {
      if (projectHasMatch(p, false)) unsortedMatch = true;
    });
    if (unsortedMatch) ids.add('unsorted-projects');

    folders.forEach((f) => folderHasMatch(f, false));
    return Array.from(ids);
  }, [searchTerm, expandedNodeIds, folders, unsortedProjects, loadedTestCases]);

  return (
    <SimpleTreeView
      aria-label="folder explorer"
      multiSelect
      selectedItems={selectedNodeIds}
      onSelectedItemsChange={onSelectedItemsChange}
      expandedItems={searchExpandedIds}
      onExpandedItemsChange={onExpandedItemsChange}
      onItemExpansionToggle={async (event: React.SyntheticEvent, nodeId: string, isExpanded: boolean) => {
        if(localStorage.getItem('showEditorwarning') === 'true'){
          return;
        }else{
          if (isExpanded) {
            try {
              const parsed = parseNodeKey(nodeId);
              if (parsed.type === 'project') {
                
                
                  if (!loadedTestCases[parsed.id]?.loading && !loadedTestCases[parsed.id]?.data) {
                    await loadProjectTestCases(parsed.id);
                  }
                
                
              }
            } catch (e) {
              // Ignore parse errors (e.g., root node, dummy nodes)
            }
          }
      }
      }}
      slots={{
        collapseIcon: FolderOpenIcon,
        expandIcon: FolderIcon,
        endIcon: FolderOutlinedIcon,
      }}
      slotProps={{
        collapseIcon: { fontSize: 'small', color: 'action' },
        expandIcon: { fontSize: 'small', color: 'action' },
        endIcon: { fontSize: 'small', color: 'action' },
      }}
      sx={{
        height: '100%',
        minWidth: 0,
        '& .MuiTreeItem-group': {
          marginLeft: 2,
          paddingLeft: 2,
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
        '& .MuiTreeItem-content': {
          minWidth: 'max-content',
          whiteSpace: 'nowrap',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          paddingTop: '2px',
          paddingBottom: '2px',
          marginBottom: '0',
          lineHeight: 1.2,
        },
        '& .MuiTreeItem-label': {
          whiteSpace: 'nowrap',
          overflow: 'visible',
        },
        '& .MuiTreeItem-content.Mui-selected': {
          backgroundColor: '#CCE8E6 !important',
          '&:hover': {
            backgroundColor: '#B3DFDC !important',
          },
        },
      }}
    >
      <TreeItem
        itemId="folder-root"
        label={<TreeItemLabel text="Lens" />}
        onContextMenu={(e) => handleContextMenu(e, 'folder-root', 'folder')}
        slots={{ icon: Inventory2OutlinedIcon }}
      >
        {loading ? (
          <CircularProgress sx={{ ml: 2, mt: 1 }} />
        ) : (
          filteredFolders.map((folder) => (
            <FolderTreeItem
              key={buildNodeKey({ type: 'folder', id: folder.folderId })}
              folder={folder}
              renamingNodeId={renamingNodeId}
              renameValue={renameValue}
              onRenameChange={onRenameChange}
              onRenameCancel={onRenameCancel}
              onRenameSubmit={onRenameSubmit}
              cutProjectIds={cutProjectIds}
              loadedTestCases={loadedTestCases}
              onContextMenu={handleContextMenu}
              onSelectedItemsChange={onSelectedItemsChange}
              filterText={filterText}
              ancestorMatched={false}
              showModifiedDates={showModifiedDates}
            />
          ))
        )}
        <TreeItem
          itemId="unsorted-projects"
          label={
            <TreeItemLabel 
              text="Unsorted Projects" 
              projectCount={unsortedProjectsStats?.projectCount}
              testCaseCount={unsortedProjectsStats?.testCaseCount}
            />
          }
          slots={{ icon: FileCopyOutlinedIcon }}
          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          {unsortedProjectsLoading ? (
            <TreeItem itemId="unsorted-projects-loading" label="Loading..." slots={{ icon: () => <CircularProgress size={16} sx={{ ml: 1 }}/> }} disabled />
          ) : filteredUnsortedProjects.length === 0 ? (
            <TreeItem itemId="unsorted-projects-empty" label="No unsorted projects" disabled />
          ) : (
            filteredUnsortedProjects.map((project) => (
              <ProjectTreeItem
                key={buildNodeKey({ type: 'project', id: project.testProjectID })}
                project={project}
                parentFolderId={undefined}
                cutProjectIds={cutProjectIds}
                loadedTestCases={loadedTestCases}
                onContextMenu={handleContextMenu}
                onSelectedItemsChange={onSelectedItemsChange}
                filterText={filterText}
                ancestorMatched={false}
                showModifiedDates={showModifiedDates}
              />
            ))
          )}
        </TreeItem>
      </TreeItem>
    </SimpleTreeView>
  );
};

export default FolderTree; 